import { useEffect, useRef, useState } from 'react'
import {
  Copy,
  Download,
  FolderArchive,
  Pencil,
  RefreshCw,
} from 'lucide-react'
import { Spinner } from '@heroui/react'
import { Button, Card } from '@trafiflow/shared-ui'

interface StoryboardHeaderProps {
  storyboardName: string
  onStoryboardNameChange: (name: string) => void
  onRegenerate?: () => void
  onCopyLink?: () => void
  onExportVideo?: () => void
  onExportClips?: () => void
  copyLabel?: string
  saveStatusIndicator?: React.ReactNode
  isExporting?: boolean
  exportingStatus?: string
  exportDisabled?: boolean
  isExportingClips?: boolean
}

export function StoryboardHeader({
  storyboardName,
  onStoryboardNameChange,
  onRegenerate,
  onCopyLink,
  onExportVideo,
  onExportClips,
  copyLabel = 'Copy Link',
  saveStatusIndicator,
  isExporting = false,
  exportingStatus = 'Exporting...',
  exportDisabled = false,
  isExportingClips = false,
}: StoryboardHeaderProps) {
  const [isEditingName, setIsEditingName] = useState(false)
  const [tempName, setTempName] = useState(storyboardName)
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false)
  const [showNamePrompt, setShowNamePrompt] = useState(false)
  const [namePromptValue, setNamePromptValue] = useState('')
  const nameInputRef = useRef<HTMLInputElement>(null)

  const isDefaultName = storyboardName === 'New Storyboard' || storyboardName.trim() === ''

  // Focus name input when prompt opens
  useEffect(() => {
    if (showNamePrompt && nameInputRef.current) {
      nameInputRef.current.focus()
    }
  }, [showNamePrompt])

  useEffect(() => {
    setTempName(storyboardName)
  }, [storyboardName])

  const handleNameSave = () => {
    if (tempName.trim()) {
      onStoryboardNameChange(tempName.trim())
    }
    setIsEditingName(false)
  }

  const handleNameCancel = () => {
    setTempName(storyboardName)
    setIsEditingName(false)
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleNameSave()
    } else if (event.key === 'Escape') {
      handleNameCancel()
    }
  }

  const handleRegenerateClick = () => {
    if (isDefaultName) {
      setNamePromptValue('')
      setShowNamePrompt(true)
    } else {
      setShowRegenerateConfirm(true)
    }
  }

  const handleNamePromptConfirm = () => {
    const trimmedName = namePromptValue.trim()
    if (!trimmedName) return

    onStoryboardNameChange(trimmedName)
    setShowNamePrompt(false)
    setShowRegenerateConfirm(true)
  }

  const handleNamePromptKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleNamePromptConfirm()
    } else if (event.key === 'Escape') {
      setShowNamePrompt(false)
    }
  }

  return (
    <>
      <header className="border-b border-tf-border flex items-center px-6 py-3 gap-4 bg-tf-bg-card">
        <div className="flex-1 flex items-center gap-2 min-w-0">
          {isEditingName ? (
            <input
              value={tempName}
              onChange={event => setTempName(event.target.value)}
              onBlur={handleNameSave}
              onKeyDown={handleKeyDown}
              className="h-9 px-3 border border-tf-border rounded-md text-xl font-semibold w-full max-w-xs"
              autoFocus
            />
          ) : (
            <>
              <h1 className="text-xl font-semibold text-tf-text truncate">
                {storyboardName}
              </h1>
              <button
                onClick={() => setIsEditingName(true)}
                className="h-7 w-7 flex items-center justify-center rounded hover:bg-tf-bg-hover text-tf-text-muted"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
              {saveStatusIndicator && (
                <div className="ml-2 pl-2 border-l border-tf-border">
                  {saveStatusIndicator}
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            icon={<RefreshCw className="h-4 w-4" />}
            onClick={handleRegenerateClick}
          >
            Regenerate
          </Button>
          <Button
            variant="secondary"
            size="sm"
            icon={<Copy className="h-4 w-4" />}
            onClick={onCopyLink}
          >
            {copyLabel}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            icon={isExportingClips ? <Spinner color="current" size="sm" /> : <FolderArchive className="h-4 w-4" />}
            onClick={onExportClips}
            disabled={exportDisabled || isExportingClips || isExporting}
          >
            {isExportingClips ? 'Exporting...' : 'Export Clips'}
          </Button>
          <Button
            variant="primary"
            size="sm"
            icon={isExporting ? <Spinner color="current" size="sm" /> : <Download className="h-4 w-4" />}
            onClick={onExportVideo}
            disabled={exportDisabled || isExporting || isExportingClips}
          >
            {isExporting ? exportingStatus : 'Export Suggested Video | Beta'}
          </Button>
        </div>
      </header>

      {showRegenerateConfirm && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => setShowRegenerateConfirm(false)}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <Card
              className="w-full max-w-md"
              onClick={event => event.stopPropagation()}
            >
              <div className="p-6 space-y-4">
                <h3 className="text-lg font-semibold text-tf-text">
                  Regenerate Storyboard
                </h3>
                <p className="text-sm text-tf-text-muted">
                  Are you sure you want to regenerate the entire storyboard?
                </p>
                <div className="flex justify-end gap-2 pt-2 border-t border-tf-border">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setShowRegenerateConfirm(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => {
                      setShowRegenerateConfirm(false)
                      onRegenerate?.()
                    }}
                  >
                    Regenerate
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </>
      )}

      {/* Name prompt modal */}
      {showNamePrompt && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => setShowNamePrompt(false)}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <Card
              className="w-full max-w-md"
              onClick={event => event.stopPropagation()}
            >
              <div className="p-6 space-y-4">
                <div className="flex items-center gap-2 text-blue-600">
                  <Pencil className="h-5 w-5" />
                  <h3 className="text-lg font-semibold text-tf-text">
                    Name Your Storyboard
                  </h3>
                </div>
                <p className="text-sm text-tf-text-muted">
                  Before continuing, please give your storyboard a name to help you identify it later.
                </p>
                <input
                  ref={nameInputRef}
                  type="text"
                  value={namePromptValue}
                  onChange={e => setNamePromptValue(e.target.value)}
                  onKeyDown={handleNamePromptKeyDown}
                  placeholder="e.g., Summer Campaign Storyboard"
                  className="w-full h-10 px-3 border border-tf-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <div className="flex justify-end gap-2 pt-2 border-t border-tf-border">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setShowNamePrompt(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    disabled={!namePromptValue.trim()}
                    onClick={handleNamePromptConfirm}
                  >
                    Continue
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </>
      )}
    </>
  )
}
